import { JWMHtml } from "@components/JWMHtml/JWMHtml";
import { JWMLink } from "@components/JWMLink/JWMLink";
import { NavigationImpression } from "@jmc/core/src/components/NavigationImpression/NavigationImpression";
import { sectionData, TableOfContents } from "@jmc/core/src/components/TableOfContents/TableOfContents";
import { TableOfContentsWrapper } from "@jmc/core/src/components/TableOfContents/TableOfContentsWrapper/TableOfContentsWrapper";
import { useHeaderProperties } from "@jmc/core/src/hooks/useHeaderProperties";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { cleanCSSIdentifier } from "@jmc/utils/utils/clean-css-identifier";
import { CMSBlock } from "@jwm/types/CMSBlock";
import { CMSFootnotes } from "@jwm/types/CMSFootnotes";
import { CMSSectionProps } from "@jwm/types/CMSSectionProps";
import React, { MutableRefObject, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUrlPrefix } from "../../contexts/SiteContext";

type PropTypes = {
    content: CMSBlock[];
    footnotes?: CMSFootnotes[];
};

export const JWMTableOfContents = (props: PropTypes): JSX.Element => {
    const { content, footnotes } = props;
    const { t } = useTranslation();
    const locale = useLocale();
    const prefix = useUrlPrefix();
    const isBrowser = typeof document !== "undefined";
    const headerProps = useHeaderProperties();
    const [isOpen, setIsOpen] = useState(false);

    const buildData = (section: CMSSectionProps, index: number): sectionData => {
        if (section?.show_title) {
            const title = (
                <JWMHtml footnotes={footnotes} color="inherit" showGlossary={false}>
                    {section?.section_title}
                </JWMHtml>
            );

            const id = section?.id || cleanCSSIdentifier(section?.section_title);
            const url = isBrowser ? decodeURI(window.location.pathname.replace(locale + "/", "")) : "";
            const link = (
                <JWMLink ariaLabel={null} external={false} url={url} anchor_id={id}>
                    <TableOfContents.Item id={id} index={index}>
                        <JWMHtml footnotes={footnotes} ignoreFootNote>
                            {section?.section_title}
                        </JWMHtml>
                    </TableOfContents.Item>
                </JWMLink>
            );

            return {
                title: title,
                id: id,
                original_title: section?.section_title,
                link: link,
            };
        }
    };

    const sections = content
        .map((block: CMSBlock, index) => {
            return buildData(block?.section, index);
        })
        .filter((item) => item?.original_title);
    const tocWrapperRef: MutableRefObject<HTMLDivElement> = useRef(null);

    return (
        <TableOfContentsWrapper
            hasTopBar={headerProps.hasTopBar}
            isCollapsed={headerProps.isCollapsed}
            isOpen={isOpen}
            ref={tocWrapperRef}
        >
            <NavigationImpression name="toc_navigation">
                <TableOfContents
                    sections={sections}
                    title={t("In this page", { ns: "navigation" })}
                    topBorder={prefix === "/"}
                    openStatus={setIsOpen}
                    openLabel={t("Open table of contents", { ns: "common" })}
                    closeLabel={t("Close table of contents", { ns: "common" })}
                />
            </NavigationImpression>
        </TableOfContentsWrapper>
    );
};

export default JWMTableOfContents;
